import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Loader2, ShieldAlert } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import useDraft from "../hooks/useDraft";
import DraftBanner from "../components/DraftBanner";
import { hasPermission } from "../lib/permissions";
import {
  CATEGORY_LABELS, CHANNEL_LABELS, announceTicketsChanged, IMPACT_LABELS, STATUS_LABELS, TICKET_HANDLER_KEYS, URGENCY_LABELS, computedPriority,
  ticketErrorMessage,
} from "../lib/tickets";
import PharmacyPicker, { canPickPharmacy } from "../components/tickets/PharmacyPicker";
import { SelectField } from "../components/tickets/TicketDialogs";
import { PriorityBadge } from "../components/tickets/TicketBits";
import { newKey } from "../components/equipment/EquipmentDialogs";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const EMPTY = { pharmacy: null, requesterName: "", requesterRole: "", requesterPhone: "", channel: "phone", category: "", impact: "", urgency: "",
                title: "", description: "", takeCharge: false };

// Nouveau ticket (D-72): a page, prefilled from a pharmacy (?prospect=). The priority is computed live and recomputed by the server.
export default function TicketFormPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromProspect = params.get("prospect");
  const fromAssist = params.get("assist"); // « Créer un ticket » after a remote assistance (D-43): linked once created
  const [assist, setAssist] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [key, setKey] = useState(newKey);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [openTickets, setOpenTickets] = useState(null);
  // A ticket opened from a pharmacy or an assistance is already filled in: only a blank form offers the draft kept on this device.
  const local = useDraft({ name: "ticket-new", userId: user?.id, values: draft, enabled: !fromProspect && !fromAssist, apply: (saved) => setDraft({ ...EMPTY, ...saved }) });
  const canHandle = hasPermission(user, TICKET_HANDLER_KEYS);
  const canPick = canPickPharmacy(user);

  // Opened from a pharmacy's « Support » tab: its name comes from the screen the reader already has the right to open.
  useEffect(() => {
    if (!fromProspect) return;
    const fromCustomers = () => api.get(`/customers/${fromProspect}`).then(({ data }) => ({ id: fromProspect, pharmacy_name: data.pharmacy.name }));
    const request = hasPermission(user, "prospects:view")
      ? api.get(`/prospects/${fromProspect}`).then(({ data }) => ({ id: data.id, pharmacy_name: data.pharmacy_name })).catch(fromCustomers)
      : fromCustomers();
    request.then((pharmacy) => setDraft((d) => (d.pharmacy ? d : { ...d, pharmacy }))).catch(() => {});
  }, [fromProspect]); // eslint-disable-line react-hooks/exhaustive-deps

  // Opened from an assistance: the reason and the person who asked prefill the ticket; the CRM pharmacy is still chosen by hand
  // (Jamora's pharmacy is not a CRM fiche). Only what the assistance page already shows this reader.
  useEffect(() => {
    if (!fromAssist) return;
    api.get(`/assist/requests/${fromAssist}`).then(({ data }) => {
      setAssist(data);
      const reason = (data.reason || "").trim();
      setDraft((d) => ({
        ...d, channel: "assist",
        title: d.title || (reason.length >= 3 ? reason.slice(0, 200) : ""),
        requesterName: d.requesterName || (data.operator || data.account || "").slice(0, 120),
      }));
    }).catch(() => toast.error("Assistance introuvable : le ticket ne lui sera pas relié."));
  }, [fromAssist]);

  // A new content is a new request: the Idempotency-Key only protects against the SAME tap sent twice.
  const change = (patch) => { setDraft((d) => ({ ...d, ...patch })); setKey(newKey()); setOpenTickets(null); setError(""); };
  const set = (name) => (value) => change({ [name]: value?.target ? value.target.value : value });
  const priority = computedPriority(draft.impact, draft.urgency);
  const valid = useMemo(() => !!draft.pharmacy && draft.requesterName.trim().length >= 2 && !!draft.category && !!draft.impact && !!draft.urgency
    && draft.title.trim().length >= 3, [draft]);

  const submit = async (confirm = false) => {
    if (!valid || saving) return;
    setSaving(true);
    setError("");
    try {
      const body = {
        prospect_id: draft.pharmacy.id, channel: draft.channel, category: draft.category, impact: draft.impact, urgency: draft.urgency,
        title: draft.title.trim(), description: draft.description.trim(),
        requester: { name: draft.requesterName.trim(), role: draft.requesterRole.trim() || null, phone: draft.requesterPhone.trim() || null },
        ...(draft.takeCharge ? { responsible_user_id: user.id } : {}), ...(confirm ? { confirm_open_tickets: true } : {}),
      };
      const { data } = await api.post("/tickets", body, { headers: { "Idempotency-Key": key } });
      if (assist) {
        try {
          await api.post(`/tickets/${data.id}/links`, { kind: "assist", ref: assist.id });
          toast.success(`Ticket ${data.number} ouvert et relié à l'assistance`);
        } catch {
          toast.warning(`Ticket ${data.number} ouvert, mais pas relié à l'assistance (il faut pouvoir traiter le ticket).`);
        }
      } else {
        toast.success(`Ticket ${data.number} ouvert`);
      }
      announceTicketsChanged();
      local.clear();
      navigate(`/support/tickets/${data.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "open_tickets") setOpenTickets(detail);
      else setError(ticketErrorMessage(e, "Impossible d'ouvrir le ticket. Réessayez."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="ticket-form-page">
      <Link to="/support/tickets" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} />Tickets</Link>
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Nouveau ticket</h1>
        <p className="text-sm text-slate-500">Notez le problème signalé par la pharmacie pour suivre sa prise en charge.</p>
        {assist && (
          <p className="text-sm text-slate-600 mt-1" data-testid="ticket-from-assist">
            Depuis l'assistance de {assist.pharmacy || "la pharmacie"} : choisissez la fiche de la pharmacie dans le CRM.
          </p>
        )}
      </div>

      <DraftBanner pending={local.pending} onRestore={local.restore} onDismiss={local.dismiss} />

      {!canPick && (
        <Card className="p-4 flex gap-3 bg-amber-50 border-amber-200">
          <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900">Pour ouvrir un ticket, il faut pouvoir consulter la pharmacie (prospects ou dossiers clients). Demandez cet accès à un administrateur.</p>
        </Card>
      )}

      <form className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]" onSubmit={(e) => { e.preventDefault(); submit(false); }}>
        <div className="space-y-4 min-w-0">
          <Card className="p-5 space-y-4">
            <h2 className="text-sm font-semibold text-slate-900">Pharmacie et demandeur</h2>
            <div className="space-y-1.5">
              <Label>Pharmacie *</Label>
              {canPick ? <PharmacyPicker value={draft.pharmacy} onChange={(pharmacy) => change({ pharmacy })} /> : <Input disabled placeholder="Accès aux pharmacies nécessaire" />}
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="ticket-requester">Demandeur *</Label>
                <Input id="ticket-requester" value={draft.requesterName} onChange={set("requesterName")} maxLength={120} placeholder="Nom" data-testid="ticket-requester" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ticket-requester-role">Fonction</Label>
                <Input id="ticket-requester-role" value={draft.requesterRole} onChange={set("requesterRole")} maxLength={80} placeholder="Pharmacien, vendeur…" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ticket-requester-phone">Numéro de rappel</Label>
                <Input id="ticket-requester-phone" value={draft.requesterPhone} onChange={set("requesterPhone")} maxLength={40} inputMode="tel" />
              </div>
            </div>
            <SelectField id="ticket-channel" label="Reçu par" value={draft.channel} onChange={set("channel")} options={CHANNEL_LABELS} />
          </Card>

          <Card className="p-5 space-y-4">
            <h2 className="text-sm font-semibold text-slate-900">Le problème</h2>
            <SelectField id="ticket-category" label="Catégorie *" value={draft.category} onChange={set("category")} options={CATEGORY_LABELS} />
            <div className="grid gap-3 sm:grid-cols-2">
              <SelectField id="ticket-impact" label="Impact *" value={draft.impact} onChange={set("impact")} options={IMPACT_LABELS} />
              <SelectField id="ticket-urgency" label="Urgence *" value={draft.urgency} onChange={set("urgency")} options={URGENCY_LABELS} />
            </div>
            <div className="text-sm text-slate-600 flex items-center gap-2" data-testid="ticket-form-priority">
              Priorité calculée : {priority ? <PriorityBadge priority={priority} /> : <span className="text-slate-400">choisissez l'impact et l'urgence</span>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ticket-title">Titre *</Label>
              <Input id="ticket-title" value={draft.title} onChange={set("title")} maxLength={120} placeholder="La caisse ne s'ouvre plus" data-testid="ticket-title" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ticket-description">Description</Label>
              <Textarea id="ticket-description" value={draft.description} onChange={set("description")} rows={5} maxLength={5000}
                placeholder="Ce que la pharmacie constate, depuis quand, ce qu'elle a déjà essayé" data-testid="ticket-description" />
              <p className="flex items-start gap-1.5 text-xs text-slate-500"><ShieldAlert size={13} className="shrink-0 mt-0.5" />Aucune donnée patient ni ordonnance.</p>
            </div>
            {canHandle && (
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox checked={draft.takeCharge} onCheckedChange={(v) => change({ takeCharge: !!v })} data-testid="ticket-take-charge" />
                Je le prends en charge
              </label>
            )}
          </Card>

          {openTickets && (
            <Card className="p-4 space-y-3 border-amber-200 bg-amber-50" data-testid="ticket-open-warning">
              <p className="text-sm font-medium text-amber-900">
                Cette pharmacie a déjà {openTickets.count} ticket{openTickets.count > 1 ? "s" : ""} ouvert{openTickets.count > 1 ? "s" : ""}.
              </p>
              {openTickets.tickets.length > 0 && (
                <ul className="space-y-1">
                  {openTickets.tickets.map((t) => (
                    <li key={t.id} className="text-sm">
                      <Link to={`/support/tickets/${t.id}`} className="text-jamora hover:underline">{t.number} · {t.title}</Link>
                      <span className="text-xs text-slate-500"> — {STATUS_LABELS[t.status]}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-amber-900">S'il s'agit du même problème, ouvrez le ticket existant et consignez-y l'échange.</p>
              <Button type="button" variant="outline" onClick={() => submit(true)} disabled={saving} data-testid="ticket-confirm-create">Créer quand même</Button>
            </Card>
          )}

          {error && <p className="text-sm text-red-600" role="alert" data-testid="ticket-form-error">{error}</p>}
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>Annuler</Button>
            <Button type="submit" disabled={!valid || saving || !canPick || !!openTickets} data-testid="ticket-form-submit">
              {saving && <Loader2 size={14} className="mr-1.5 animate-spin" />}Ouvrir le ticket
            </Button>
          </div>
        </div>

        <aside className="hidden lg:block space-y-3">
          <Card className="p-4 space-y-2 text-sm text-slate-600">
            <h2 className="font-semibold text-slate-900">Priorité et délais</h2>
            <p>La priorité vient de l'impact et de l'urgence. Elle fixe le délai de première réponse et celui de résolution (Paramètres › Support).</p>
            <p className="text-xs">Une pharmacie bloquée qui ne peut plus vendre est <PriorityBadge priority="critical" /> ; une gêne mineure qui peut
              attendre est <PriorityBadge priority="low" />.</p>
          </Card>
          <Card className="p-4 text-sm text-slate-600 space-y-1">
            <h2 className="font-semibold text-slate-900">Ensuite</h2>
            <p>Consignez chaque échange avec la pharmacie : le premier échange sortant compte comme première réponse.</p>
          </Card>
        </aside>
      </form>
    </div>
  );
}
